import { useState, useEffect, useCallback, Fragment } from 'react';
import { useSelector } from 'react-redux';
import OverallCompletion from '../components/OverallCompletion';
import CompletionBreakdown from '../components/CompletionBreakdown';
import ExpandableGeneric from '../components/ExpandableGeneric';
import ExpandableGenericDynamic from '../components/ExpandableGeneric/ExpandableGenericDynamic';
import StandardsDashboardElementary from '../components/StandardsDashboardElementary';
import SpinnerOverlay from '../SpinnerOverlay';
import {
  getElementaryCompletion,
  getSupplementaryCompletion,
  getNumModules,
  getNumCompleteModules,
  getUnitCompletion,
  getUnits,
  getAllApplicableStandards,
  getTotalCompletedStandards,
  getCompletedLessonsWithStandards,
} from './elementaryCompletionHelpers';
import { selectClassroom } from '../Classrooms';
import { RootState } from '../ApplicationState';
import styles from './CurriculumElementaryDashboard.module.scss';
import { ElementaryUnit } from '@peekapak/types/DataProtocolTypes';

const CurriculumElementaryDashboard = (): JSX.Element => {
  const [isCurriculumExpanded, setIsCurriculumExpanded] = useState(false);
  const [isStandardsExpanded, setIsStandardsExpanded] = useState(false);
  const [isSupplementaryExpanded, setIsSupplementaryExpanded] = useState(false);
  const [units, setUnits] = useState<ElementaryUnit[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [resizeTrigger, triggerResize] = useState(false);
  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });
  const completionStatus = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom?.completionStatus;
  });

  const grade = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom?.grade;
  });

  const expandUnits = () => {
    setIsCurriculumExpanded((prevState) => !prevState);
  };
  const expandStandards = () => {
    setIsStandardsExpanded((prevState) => !prevState);
  };
  const expandSupplementary = () => {
    setIsSupplementaryExpanded((prevState) => !prevState);
  };
  const handleResize = () => {
    triggerResize((prevState) => !prevState);
  };

  const elementaryCompletion = completionStatus && getElementaryCompletion(completionStatus);
  const totalCurriculumModules = elementaryCompletion && getNumModules(elementaryCompletion);
  const totalCompleteModules = elementaryCompletion && getNumCompleteModules(elementaryCompletion);
  const elementaryUnitCompletion = elementaryCompletion && getUnitCompletion(elementaryCompletion, grade);

  const supplementaryCompletion = completionStatus && getSupplementaryCompletion(completionStatus, grade);
  const totalSupplementaryModules = supplementaryCompletion && getNumModules(supplementaryCompletion);
  const totalCompleteSupplementaryModules = supplementaryCompletion && getNumCompleteModules(supplementaryCompletion);
  const supplementaryUnitCompletion = supplementaryCompletion && getUnitCompletion(supplementaryCompletion, grade);

  const allApplicableStandards = units && getAllApplicableStandards(units);
  const uniqueStandards = allApplicableStandards && [...new Set(allApplicableStandards)];
  const totalStandards = uniqueStandards?.length || 1;

  const [completedLessonsWithStandards, incompleteLessonsWithStandards] =
    elementaryCompletion && units
      ? getCompletedLessonsWithStandards(elementaryCompletion, units, classroom.className)
      : [];

  const totalCompleteStandards =
    completedLessonsWithStandards && getTotalCompletedStandards(completedLessonsWithStandards);

  const getStandardsData = useCallback(async () => {
    const lessonData = await getUnits(classroom.language, grade);
    setUnits(lessonData);
    console.log(lessonData);
  }, [classroom, grade]);

  useEffect(() => {
    elementaryCompletion && setIsLoaded(true);
    grade !== undefined && getStandardsData();
  }, [classroom, grade]);

  return (
    <div className={styles.container}>
      <SpinnerOverlay isShow={!isLoaded} />
      <div className={styles.titleBar}>
        <h2 className={styles.reportTitle}>Curriculum Completion</h2>
      </div>
      <div className={styles.dataContainer}>
        {isLoaded && (!elementaryCompletion || Object.keys(elementaryCompletion).length === 0) ? (
          <div className={styles.noData}>
            This class does not have any completion data. Report will be generated as lessons are completed.
          </div>
        ) : (
          <Fragment>
            <OverallCompletion
              title='Curriculum'
              handleClick={expandUnits}
              numComplete={totalCompleteModules || 0}
              totalNum={totalCurriculumModules}
              textButton={`${isCurriculumExpanded ? 'Hide' : 'Show'} Unit Detail`}
              barColor='#ff6319'
              isExpanded={isCurriculumExpanded}
            />

            <ExpandableGeneric isExpanded={isCurriculumExpanded}>
              {elementaryCompletion && (
                <CompletionBreakdown
                  colA='Unit Name'
                  colB='Unit Progress'
                  colC='Completion'
                  data={elementaryUnitCompletion}
                  barColor='#ff6319'
                  isShouldAnimate={isCurriculumExpanded}
                />
              )}
            </ExpandableGeneric>
            {supplementaryCompletion && (
              <Fragment key={'supplementary completion'}>
                <OverallCompletion
                  title='Supplementary Units'
                  handleClick={expandSupplementary}
                  numComplete={totalCompleteSupplementaryModules || 0}
                  totalNum={totalSupplementaryModules}
                  textButton={`${isSupplementaryExpanded ? 'Hide' : 'Show'} Unit Detail`}
                  barColor='#ff6319'
                  isExpanded={isSupplementaryExpanded}
                />

                <ExpandableGeneric isExpanded={isSupplementaryExpanded}>
                  <CompletionBreakdown
                    colA='Unit Name'
                    colB='Unit Progress'
                    colC='Completion'
                    data={supplementaryUnitCompletion}
                    barColor='#ff6319'
                    isShouldAnimate={isSupplementaryExpanded}
                  />
                </ExpandableGeneric>
              </Fragment>
            )}
            <OverallCompletion
              numComplete={totalCompleteStandards || 0}
              totalNum={totalStandards}
              title='ELA Standards'
              handleClick={expandStandards}
              textButton={`${isStandardsExpanded ? 'Hide' : 'Show'} Detail`}
              barColor='#ff6319'
              isExpanded={isStandardsExpanded}
            />
            <ExpandableGenericDynamic isExpanded={isStandardsExpanded} resizeTrigger={resizeTrigger}>
              {elementaryCompletion && classroom && (
                <StandardsDashboardElementary
                  allStandards={allApplicableStandards}
                  handleResize={handleResize}
                  classroom={classroom}
                  completedLessons={completedLessonsWithStandards}
                  incompleteLessons={incompleteLessonsWithStandards}
                />
              )}
            </ExpandableGenericDynamic>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default CurriculumElementaryDashboard;
